import React, { useState } from 'react'
import { Tooltip, Typography, withStyles } from '@material-ui/core'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getCardAction } from 'src/reducers/card/action'
import {
  CardPublishState,
  useCoursePublish,
} from 'src/contexts/course-publish-context'
import styles from './styles'

/**
 * A standing list of everything not yet live, at the top of the table of contents.
 *
 * The per-page markers alone make you scan the whole outline to answer "what have I not published
 * yet?" — fine for three pages, useless for thirty. This answers it in one place, and each entry
 * jumps to its page.
 */
const PublishSummary = props => {
  const { classes } = props
  const publishState = useCoursePublish()
  const course = useSelector(state => state.course.course)
  const open = useSelector(state => state.course.sectionDrawerOpen)
  const history = useHistory()
  const dispatch = useDispatch()

  // Bulk-adding questions can leave a dozen pending pages here, and the panel pushes the outline
  // itself off the bottom of the list. Collapsing keeps the counts, which are the part worth
  // seeing at a glance, and gives the outline its room back.
  const [collapsed, setCollapsed] = useState(false)

  // While the whole course is a draft, listing individual pages is noise: none of them are
  // visible to anyone regardless of their own state.
  if (!open || !publishState?.isPublished) {
    return null
  }

  const pending = publishState.pendingPages

  if (pending.length === 0) {
    return null
  }

  const goToPage = cardId => {
    history.push(`/mini-course/${course.id}/page/${cardId}`)
    dispatch(getCardAction(cardId))
  }

  const draftCount = pending.filter(
    x => x.state === CardPublishState.DRAFT,
  ).length
  const editedCount = pending.length - draftCount

  // "draft", not "not published": the badge on every row below says Draft, and the state should
  // not pick up a second name in the one place that counts them.
  const summary = [
    draftCount > 0 && `${draftCount} draft`,
    editedCount > 0 && `${editedCount} edited`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={classes.root}>
      <div
        className={clsx(classes.header, {
          [classes.headerCollapsed]: collapsed,
        })}
        onClick={() => setCollapsed(current => !current)}
      >
        <span className={classes.dot} />
        <Typography className={classes.headerText}>{summary}</Typography>
        <Tooltip
          title={collapsed ? 'Show the list' : 'Hide the list'}
          placement="top"
        >
          <Icon
            className={classes.toggleIcon}
            icon={collapsed ? 'eva:chevron-down-fill' : 'eva:chevron-up-fill'}
            width={16}
            height={16}
          />
        </Tooltip>
      </div>

      {!collapsed &&
        pending.map(page => (
          <div
            key={page.cardId}
            className={classes.row}
            onClick={() => goToPage(page.cardId)}
          >
            <Typography className={classes.pageTitle} noWrap>
              {page.title || 'Untitled Page'}
            </Typography>
            {page.state === CardPublishState.DRAFT ? (
              <Tooltip title="Not visible to learners yet" placement="top">
                <span className={classes.draftBadge}>Draft</span>
              </Tooltip>
            ) : (
              <Tooltip title="Unpublished changes" placement="top">
                <span className={classes.editedBadge}>Edited</span>
              </Tooltip>
            )}
          </div>
        ))}
    </div>
  )
}

export default withStyles(styles)(PublishSummary)
